import React, { useRef, useCallback } from 'react';
import { useToggle } from 'app/utils/hook/hooks';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import { cut } from 'app/utils/string/string-utils';
import { Menu, IconButton, MdiIcon, Slider, Tooltip, Box } from '@mic3/platform-ui';
import MenuItem from 'app/components/molecules/Menu/MenuItem';
import cesiumLayerConfig from 'app/config/cesiumLayerConfig';
import { toggleAddPinBtn, updateMapLayers } from 'store/actions/maps/situationalAwarenessActions';
import { useDispatch } from 'react-redux';
import { isEmpty } from 'app/utils/utils';
import { __getMapLayers } from 'app/utils/maps/layer/layerUtils';

const StyledBox = styled(Box)`
    display: flex;
    align-items: center;
`;
const OpacityIconButton = styled(IconButton)`
    font-size: 1rem !important;
    width: 30px !important;
    height: 30px !important;
    margin-right: 12px !important;
    padding: 24px !important;
    color: ${({ theme }) => theme.material.colors.text.secondary};
`;

const SliderWrapper = styled(Slider)`
    width: 150px !important;
`;

const VisibilityIcon = styled(MdiIcon)`
    cursor: pointer;
    padding: 24px;
    width: 30px;
    height: 30px;
    &:before {
        color: ${({ theme }) => theme.material.colors.text.secondary};
    }
    &:hover {
        border-radius: 50%;
        background-color: ${({ theme }) => theme.material.colors.background.fields};
    }
`;

const MOVE_ACTIONS = [
    { key: 'top', icon: 'chevron-double-up', label: 'Move to top' },
    { key: 'up', icon: 'chevron-up', label: 'Move up' },
    { key: 'down', icon: 'chevron-down', label: 'Move down' },
    { key: 'bottom', icon: 'chevron-double-down', label: 'Move to bottom' },
];

const MoveLayerMenu = ({ open, onClose, onMove, anchorEl }) => (
    <Menu 
        open={open} 
        onClose={onClose} 
        title="Move Layer"
        anchorEl={anchorEl}
    >
        {MOVE_ACTIONS.map(action => (
            <MenuItem
                key={action.key}
                icon={action.icon}
                name={action.label}
                onClick={() => {
                    onMove(action.key);
                    onClose();
                }}
            />
        ))}
    </Menu>
);

const getMenuConfig = ({ subtitle, type, title, canEdit, cut, handleMenuItemClick, handleMoveLayerClick }) => {
    const config = [
        {
            key: 'about',
            show: true,
            onClick: e => handleMenuItemClick(e, 'About'),
            name: 'About Layer',
            icon: 'information-outline',
        },
        {
            key: 'zoom',
            show: subtitle === 'entities' && type !== 'replay',
            onClick: e => handleMenuItemClick(e, 'Zoom'),
            name: `Zoom to layer ${cut(title, 16)}`,
            icon: 'magnify-plus-outline',
        },
        {
            key: 'move',
            show: canEdit,
            onClick: handleMoveLayerClick,
            name: 'Move Layer',
            icon: 'arrow-up-down',
        },
        {
            key: 'remove',
            show: canEdit,
            onClick: e => handleMenuItemClick(e, 'Remove'),
            name: 'Remove Layer',
            icon: 'delete',
        },
    ];
    return config.filter(item => item.show);
};

const BuildMoreMenu = ({ 
    map, 
    title, 
    mapId, 
    layer, 
    onLayerRemove, 
    openLayerSidebar, 
    canEdit,
    mapData,
    updateMapData
}) => {    
    const anchorEl = useRef(null);
    const [isOpen, toggleMenu] = useToggle(false);
    const [moveMenuOpen, setMoveMenuOpen] = React.useState(false);

    const { values_ } = layer || {};
    const { attributes } = values_ || {};
    const { subtitle, id, layerType, type } = attributes || {};

    const dispatch = useDispatch();

    const handleMenuItemClick = useCallback(
        (event, title) => {
            event.stopPropagation();
            dispatch(toggleAddPinBtn(false));

            switch (title) {
                case 'About':
                    return openLayerSidebar({ id, title: layerType, type, layer, mapId, map, layerName: title });
                case 'Zoom':
                    return map && map.zoomToMapLayer && map.zoomToMapLayer(layer);
                case 'Remove':
                    onLayerRemove({ id, name: values_?.title || values_?.attributes?.name });
                    return toggleMenu();
                default:
                    break;
            }
        },
        [map, id, layer, layerType, mapId, onLayerRemove, openLayerSidebar, toggleMenu, type, dispatch, values_]
    );

    const handleMoveLayerClick = useCallback(
        (e) => {
            e.stopPropagation();
            setMoveMenuOpen(true);
            toggleMenu();
        },
        [toggleMenu]
    );

    const handleMove = useCallback(
        (moveType) => {
            if (!id || !map) return;
            
            const allMapLayers = __getMapLayers(mapData);
            if (!allMapLayers || !allMapLayers.length) {
                return;
            }
            
            const currentIndex = allMapLayers.findIndex(l => l.id === id);
            if (currentIndex === -1) {
                return;
            }
            
            const layersCopy = [...allMapLayers];
            const currentLayer = layersCopy[currentIndex];
            
            switch (moveType) {
                case 'top':
                    if (currentIndex === 0) return;
                    layersCopy.splice(currentIndex, 1);
                    layersCopy.unshift(currentLayer);
                    break;
                    
                case 'up':
                    if (currentIndex === 0) return;
                    layersCopy.splice(currentIndex, 1);
                    layersCopy.splice(currentIndex - 1, 0, currentLayer);
                    break;
                    
                case 'down':
                    if (currentIndex === layersCopy.length - 1) return;
                    layersCopy.splice(currentIndex, 1);
                    layersCopy.splice(currentIndex + 1, 0, currentLayer);
                    break;
                    
                case 'bottom':
                    if (currentIndex === layersCopy.length - 1) return;
                    layersCopy.splice(currentIndex, 1);
                    layersCopy.push(currentLayer);
                    break;
                    
                default:
                    return;
            }
        
            dispatch(updateMapLayers(layersCopy));
            if (map && typeof map.reorderLayersByIdList === 'function') {
                const newOrderIds = layersCopy.map(l => l.id);
                map.reorderLayersByIdList(newOrderIds);
            }
        },
        [map, id, mapData, dispatch]
    );
    
    const menuConfig = getMenuConfig({
        subtitle,
        type,
        title,
        canEdit,
        cut,
        handleMenuItemClick,
        handleMoveLayerClick
    });

    return (
        <>
            <IconButton buttonRef={anchorEl} onClick={toggleMenu}>
                <MdiIcon name="dots-vertical" />
            </IconButton>
            <Menu open={isOpen} anchorEl={anchorEl.current} onClose={toggleMenu}>
                {menuConfig.map(item => (
                    <MenuItem
                        key={item.key}
                        onClick={item.onClick}
                        name={item.name}
                        icon={item.icon}
                    />
                ))}
            </Menu>
            <MoveLayerMenu 
                anchorEl={anchorEl.current}
                open={moveMenuOpen} 
                onClose={() => setMoveMenuOpen(false)} 
                onMove={handleMove} 
            />
        </>
    );
};

const BuildOpacityMenu = ({ name, opacity: op, onOpacityChange, type }) => {
    const anchorEl = useRef(null);
    const [isOpen, toggleMenu] = useToggle(false);
    const opacity = op === 100 ? 1 : op;
    const isIonLayer = cesiumLayerConfig?.cesiumLayer?.isCesiumLayer(type);
    return (
        <>
            {' '}
            {!isIonLayer && (
                <Tooltip arrow title={'Change layer opacity'} placement="top-start">
                    <span>
                        <OpacityIconButton buttonRef={anchorEl} onClick={toggleMenu}>
                            {opacity * 100}%
                        </OpacityIconButton>
                    </span>
                </Tooltip>
            )}
            <Menu open={isOpen} anchorEl={anchorEl.current} onClose={toggleMenu}>
                <MenuItem onClick={event => event.stopPropagation()}>
                    <SliderWrapper name={name} value={opacity} onChange={onOpacityChange} min={0} max={1} step={0.1} />
                </MenuItem>
            </Menu>
        </>
    );
};

const LayerListItemActions = props => {
    const { 
        layer, 
        map, 
        mapId, 
        onLayerRemove, 
        toggleMapLayer, 
        onOpacityChange, 
        openLayerSidebar, 
        canEdit, 
        item,
        mapData,
        updateMapData 
    } = props;

    const { values_ } = layer || item || {};
    const { visible, title, opacity, name, attributes } = isEmpty(values_) ? item : values_;
    const { id, type } = attributes || {};
    if (isEmpty(values_) && isEmpty(item)) {
        return null;
    }
    const layerId = id || item?.id;
    let visibility = visible;
    if (title) {
        const entityLayer = map.findLayerById(layerId);
        const is3DEnabled = map?.is3dEnabled?.() || false;
        
        if (is3DEnabled && entityLayer?.values_?.visible3d !== undefined) {
            visibility = entityLayer.values_.visible3d;
        } else {
            visibility = entityLayer?.values_?.visible ?? visible;
        }
    }

    return (
        <StyledBox>
            <BuildOpacityMenu name={title} opacity={opacity} onOpacityChange={event => onOpacityChange(layerId, event)} type={type} />
            <Tooltip arrow title={`Click on it to ${visibility ? 'hide' : 'show'} '${title}' layer`} placement="top-start">
                <span>
                    <VisibilityIcon name={visibility ? 'eye' : 'eye-off'} onClick={event => toggleMapLayer(event, 'allLayers', title, !visibility, name, id)} />
                </span>
            </Tooltip>
            <BuildMoreMenu
                map={map}
                title={title}
                canEdit={canEdit}
                mapId={mapId}
                onLayerRemove={onLayerRemove}
                layer={layer}
                openLayerSidebar={openLayerSidebar}
                mapData={mapData}
                updateMapData={updateMapData}
            />
        </StyledBox>
    );
};

LayerListItemActions.propTypes = {
    layer: PropTypes.object,
    item: PropTypes.object,
    map: PropTypes.object.isRequired,
    onLayerRemove: PropTypes.func.isRequired,
    toggleMapLayer: PropTypes.func.isRequired,
    onOpacityChange: PropTypes.func.isRequired,
    openLayerSidebar: PropTypes.func.isRequired,
    mapId: PropTypes.string.isRequired,
    canEdit: PropTypes.bool,
    mapData: PropTypes.object,
    updateMapData: PropTypes.func
};

export default LayerListItemActions;
